import { useQuery } from "@apollo/client";
import { useState } from "react";
import { FaDiscord, FaSearch } from "react-icons/fa";
import { GET_REIN_USERS } from "../../../../queries/mutation/broadcaster/queries";
import { ReinUser } from "../../../../types/ReinUser";
import LoadingPage from "../../../LoadingPage";
import ReinUserProfile from "../ReinUserProfile";
import { formatDate, parseDate } from "./format";
import StatTile from "./StatTile";

const PAGE_SIZE = 100;

const time = (value?: string | number | null) =>
  parseDate(value)?.getTime() ?? 0;

const SORTS: {
  value: string;
  label: string;
  compare: (a: ReinUser, b: ReinUser) => number;
}[] = [
  {
    value: "joined",
    label: "Newest members",
    compare: (a, b) => time(b.createdAt) - time(a.createdAt),
  },
  {
    value: "crimcoins",
    label: "Most crimcoins",
    compare: (a, b) => b.discordTokens - a.discordTokens,
  },
  {
    value: "tips",
    label: "Most tipped on CB",
    compare: (a, b) => (b.lifetimeTips ?? -1) - (a.lifetimeTips ?? -1),
  },
  {
    value: "discordLinked",
    label: "Recently linked Discord",
    compare: (a, b) => time(b.discordLinkedAt) - time(a.discordLinkedAt),
  },
  {
    value: "name",
    label: "Username A-Z",
    compare: (a, b) => a.username.localeCompare(b.username),
  },
];

const UsersTab = () => {
  const [search, setSearch] = useState("");
  const [discordOnly, setDiscordOnly] = useState(false);
  const [cbOnly, setCbOnly] = useState(false);
  const [sort, setSort] = useState("joined");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(GET_REIN_USERS, {
    fetchPolicy: "cache-and-network",
  });
  const users: ReinUser[] = data?.getReinUsers ?? [];

  // Looked up from the latest data rather than kept as a snapshot, so the
  // profile shows the new balance after crediting coins.
  const selected = users.find((u) => u.id === selectedId) ?? null;

  const term = search.trim().toLowerCase();
  const compare = (SORTS.find((s) => s.value === sort) ?? SORTS[0]).compare;
  const filtered = users
    .filter((u) => !discordOnly || u.discordProfile)
    .filter((u) => !cbOnly || u.cbUsername)
    .filter(
      (u) =>
        !term ||
        u.username.toLowerCase().includes(term) ||
        u.cbUsername?.toLowerCase().includes(term),
    )
    .sort(compare);
  const visible = filtered.slice(0, limit);

  const withDiscord = users.filter((u) => u.discordProfile).length;
  const withCb = users.filter((u) => u.cbUsername).length;
  const totalCrimcoins = users.reduce((sum, u) => sum + u.discordTokens, 0);

  const list = (
    <div
      className={
        selected
          ? "hidden"
          : "card bg-base-100 flex-1 min-h-0 flex flex-col shadow"
      }
    >
      <div className="card-body flex flex-col min-h-0 !p-2 sm:!p-4 gap-3">
        {users.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatTile label="Users" value={users.length} />
            <StatTile
              label="Discord linked"
              value={withDiscord}
              hint={`${Math.round((withDiscord / users.length) * 100)}%`}
              className="text-info"
            />
            <StatTile
              label="CB linked"
              value={withCb}
              hint={`${Math.round((withCb / users.length) * 100)}%`}
              className="text-warning"
            />
            <StatTile
              label="Crimcoins held"
              value={totalCrimcoins}
              hint={`avg ${Math.round(totalCrimcoins / users.length).toLocaleString()}`}
              className="text-secondary"
            />
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <label className="input input-bordered input-sm flex items-center gap-2 flex-1">
            <FaSearch className="opacity-50" />
            <input
              type="search"
              className="grow"
              placeholder="Search username or CB username"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setLimit(PAGE_SIZE);
              }}
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              className={`btn btn-sm ${discordOnly ? "btn-info" : "btn-outline"}`}
              onClick={() => setDiscordOnly(!discordOnly)}
              aria-pressed={discordOnly}
            >
              <FaDiscord /> Discord
            </button>
            <button
              className={`btn btn-sm ${cbOnly ? "btn-warning" : "btn-outline"}`}
              onClick={() => setCbOnly(!cbOnly)}
              aria-pressed={cbOnly}
            >
              CB linked
            </button>
            <select
              className="select select-bordered select-sm"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort users"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading && !data ? (
          <div className="flex-1 flex items-center justify-center">
            <LoadingPage />
          </div>
        ) : error && !data ? (
          <div className="text-center py-8">
            <div className="text-error font-bold mb-2">Couldn't load users</div>
            <div className="text-sm text-base-content/60 mb-3">
              {error.message}
            </div>
            <button className="btn btn-sm" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-8 text-base-content/60">
            No users match{term ? ` "${search.trim()}"` : " these filters"}
          </div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="table table-sm table-pin-rows">
              <thead>
                <tr>
                  <th>User</th>
                  <th className="hidden sm:table-cell">CB account</th>
                  <th className="hidden sm:table-cell">Discord</th>
                  <th className="text-right">Crimcoins</th>
                  <th className="hidden md:table-cell">Joined</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr
                    key={u.id}
                    className="hover cursor-pointer"
                    tabIndex={0}
                    onClick={() => setSelectedId(u.id)}
                    onKeyDown={(e) => e.key === "Enter" && setSelectedId(u.id)}
                  >
                    <td>
                      <div className="font-semibold flex items-center gap-1 break-all">
                        {u.username}
                        {u.discordProfile && (
                          <FaDiscord
                            className="text-info sm:hidden shrink-0"
                            title="Discord linked"
                          />
                        )}
                      </div>
                      {/* The CB account column is hidden on phones. */}
                      {u.cbUsername && (
                        <div className="sm:hidden text-xs text-warning break-all">
                          CB: {u.cbUsername} ·{" "}
                          {(u.lifetimeTips ?? 0).toLocaleString()} tokens
                        </div>
                      )}
                    </td>
                    <td className="hidden sm:table-cell">
                      {u.cbUsername ? (
                        <div>
                          <div className="text-warning">{u.cbUsername}</div>
                          <div className="text-xs text-base-content/60 flex gap-1 items-center">
                            {(u.lifetimeTips ?? 0).toLocaleString()} tokens
                            {u.inFanclub && (
                              <span className="badge badge-success badge-xs">
                                fan club
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-base-content/40">—</span>
                      )}
                    </td>
                    <td className="hidden sm:table-cell text-sm">
                      {u.discordProfile ? (
                        <div className="flex items-center gap-1">
                          <FaDiscord className="text-info" />
                          {formatDate(u.discordLinkedAt)}
                        </div>
                      ) : (
                        <span className="text-base-content/40">—</span>
                      )}
                    </td>
                    <td className="text-right font-semibold text-secondary">
                      {u.discordTokens.toLocaleString()}
                    </td>
                    <td className="hidden md:table-cell text-sm">
                      {formatDate(u.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {visible.length > 0 && (
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-base-content/60">
              Showing {visible.length} of {filtered.length}
            </span>
            {visible.length < filtered.length && (
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setLimit(limit + PAGE_SIZE)}
              >
                Show more
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {list}
      {selected && (
        <ReinUserProfile
          reinUser={selected}
          refreshTipper={() => refetch()}
          clearUser={() => setSelectedId(null)}
        />
      )}
    </>
  );
};

export default UsersTab;
